import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DownloadDropdown, InfoTooltip } from "../../Components";
import { SnapshotChart } from "./Components/SnapshotChart";
import WidgetContainer from "../../Components/WidgetContainer";
import { Column, DimensionColumn } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import * as R from "ramda";
import { currencyFormatter, snapShotDataSort } from "../../MMM/MMMUtils";
import ChartContainer from "../../Components/ChartContainer";
import MetricsTable from "../../SingleChannel/MetricsTable/MetricsTable";
import "./NextMediaDollar.scss";
import { formatROITableData, formatSnapshotData } from "../DataFormatters";
import { useCompanyInfo } from "../../redux/company";
import { exportToExcel } from "../../utils/download-utils";

const TABLE_HEADERS: Column[] = [
  {
    id: "previousQuarterROILow",
    dataVarName: "roi",
    label: "Previous quarter ROAS low",
  },
  {
    id: "previousQuarterROI",
    dataVarName: "roi",
    label: "Previous quarter ROAS",
  },
  {
    id: "previousQuarterROIHigh",
    dataVarName: "roi",
    label: "Previous Quarter ROAS high",
  },
  {
    id: "entireTimePeriodROILow",
    dataVarName: "roi",
    label: "Entire time period ROAS low",
  },
  {
    id: "entireTimePeriodROI",
    dataVarName: "roi",
    label: "Entire time period ROAS",
  },
  {
    id: "entireTimePeriodROIHigh",
    dataVarName: "roi",
    label: "Entire time period ROAS high",
  },
  {
    id: "previousQuarterMedianSpend",
    dataVarName: "spend",
    label: "Previous quarter median spend",
  },
  {
    id: "predictedResponseToMedianSpendLow",
    dataVarName: "response",
    label: "Predicted response to median spend low",
  },
  {
    id: "predictedResponseToMedianSpend",
    dataVarName: "response",
    label: "Predicted response to median spend",
  },
  {
    id: "predictedResponseToMedianSpendHigh",
    dataVarName: "response",
    label: "Predicted response to median spend high",
  },
  {
    id: "nextDollarEffectiveness",
    dataVarName: "nextDollarEffectiveness",
    label: "Next Dollar Effectiveness",
    heatMapping: {
      colorScheme: "sequential",
    } as any,
  },
];

const ROW_HEIGHT = 60;
const TABLE_HEIGHT = 320;

interface NextMediaDollarProps {
  roiTableRows: any[];
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
  outcomeVariable: "revenue" | "kpiVolume";
  maxDimension: string;
}

export const NextMediaDollar: React.FC<NextMediaDollarProps> = ({
  roiTableRows,
  channelBreakoutMap,
  outcomeVariable,
  maxDimension,
}) => {
  const [snapChartSortValue, setSnapChartSortValue] = useState("Highest to lowest");
  const [tableWidth, setTableWidth] = useState(500);
  const [rerenderTable, setRerenderTable] = useState(false);
  const containerRef = useRef(null);
  const { cid: company } = useCompanyInfo();

  const windowResizeFunction = () => setRerenderTable(true);
  if (window && window.onresize !== windowResizeFunction) {
    window.onresize = windowResizeFunction;
  }

  const channelBreakoutHeaderLabel = useMemo(() => {
    switch (maxDimension) {
      case "Platform":
        return "Channel, Platform";
      case "Tactic":
        return "Channel, Platform, Tactic";
      case "Brand/Non-Brand":
        return "Channel, Platform, Tactic, Brand/Non-Brand";
      default:
        return "Channel";
    }
  }, [maxDimension]);

  const DimensionColumns: DimensionColumn[] = useMemo(
    () => [
      {
        id: "name",
        dimensionVarName: "Channel",
        label: channelBreakoutHeaderLabel,
        dimensionTypeName: "Channel",
        divider: false,
      },
    ],
    [channelBreakoutHeaderLabel]
  );

  useLayoutEffect(() => {
    const newTableWidth = containerRef.current
      ? (containerRef.current as any).offsetWidth
      : undefined;
    if (newTableWidth) {
      setTableWidth((containerRef.current as any).offsetWidth - 5);
    }
    if (rerenderTable) {
      setRerenderTable(false);
    }
  }, [rerenderTable]);

  const { tableHeaders, snapChartData, roiTableData, dimensionData } = useMemo(() => {
    const output = {
      tableHeaders: [] as Column[],
      snapChartData: [] as any[],
      roiTableData: [] as any[],
      dimensionData: [] as any[],
    };

    if (!R.isEmpty(roiTableRows) && !R.isEmpty(R.keys(channelBreakoutMap))) {
      output.snapChartData = snapShotDataSort(formatSnapshotData(roiTableRows), snapChartSortValue);

      // Set labels based on outcomeVariable - change ROAS to CPA for kpiVolume
      output.tableHeaders = TABLE_HEADERS.map(item => {
        if (item.label && item.label.includes("ROAS")) {
          const roiType = outcomeVariable === "revenue" ? "ROAS" : "CPA";
          return {
            ...item,
            label: item.label.replace("ROAS", roiType),
          };
        }
        return item;
      });

      // Format raw sheet row data to match rMMM data
      const combinedDataMap: { [breakout: string]: any } = {};
      roiTableRows.forEach(row => {
        const {
          channel: breakout,
          "Lower Bound (Marg)": ciLow,
          "Upper Bound (Marg)": ciHigh,
          "Lower Bound (LQ)": previousQuarterROILow,
          "Mean (LQ)": previousQuarterROI,
          "Upper Bound (LQ)": previousQuarterROIHigh,
          "Lower Bound": entireTimePeriodROILow,
          Mean: entireTimePeriodROI,
          "Upper Bound": entireTimePeriodROIHigh,
          median_spend: previousQuarterMedianSpend,
          pred_median_low: predictedResponseToMedianSpendLow,
          pred_median: predictedResponseToMedianSpend,
          pred_median_high: predictedResponseToMedianSpendHigh,
          "Next Best Dollar Effectiveness": nextDollarEffectiveness,
        } = row;

        if (channelBreakoutMap[breakout.toLowerCase()]) {
          const { channel, platform, tactic, brand_nonbrand } = channelBreakoutMap[
            breakout.toLowerCase()
          ];
          combinedDataMap[breakout] = {
            name: breakout,
            channel,
            platform,
            tactic,
            brand_nonbrand,
            ciLow,
            ciHigh,
            previousQuarterROILow,
            previousQuarterROI,
            previousQuarterROIHigh,
            entireTimePeriodROILow,
            entireTimePeriodROI,
            entireTimePeriodROIHigh,
            previousQuarterMedianSpend,
            predictedResponseToMedianSpendLow,
            predictedResponseToMedianSpend,
            predictedResponseToMedianSpendHigh,
            nextDollarEffectiveness,
          };
        }
      });

      output.roiTableData = formatROITableData({
        data: R.values(combinedDataMap),
        tableHeaders: output.tableHeaders,
        outcomeVariable,
      });
      output.dimensionData = formatROITableData({
        data: R.values(combinedDataMap),
        tableHeaders: DimensionColumns,
        outcomeVariable,
      });
    }

    return output;
  }, [channelBreakoutMap, roiTableRows, snapChartSortValue, outcomeVariable, DimensionColumns]);

  const excelDownloadROI = useCallback(() => {
    // Filter out __EMPTY fields that can cause Excel export issues
    const cleanData = roiTableRows.map(row => {
      const cleanRow = { ...row };
      delete cleanRow.__EMPTY;
      return cleanRow;
    });
    exportToExcel(cleanData, `${company}_${outcomeVariable === "revenue" ? "ROAS" : "CPA"}`);
  }, [company, outcomeVariable, roiTableRows]);

  return (
    <WidgetContainer
      collapsible
      header="Where should we allocate our next media dollar?"
      subHeader={
        <>
          Below is the <i style={{ whiteSpace: "pre-wrap" }}> marginal </i> impact of each media
          dollar, given the existing allocation of resources.
          <InfoTooltip rightLabel="More info" size="sm">
            The marginal impact of a media dollar is based on where one is on the channel saturation
            curve (see section below). Next dollar effectiveness is the slope of the line tangent to
            the saturation curve at the current level of investment. Intuitively, you want to invest
            where the slope of the curve is steepest, i.e. offers the highest return.
          </InfoTooltip>
        </>
      }
    >
      <div className="nextMediaDollarSection">
        <div className="left">
          {!R.isEmpty(snapChartData) && (
            <SnapshotChart
              title="Estimated Return on Next Invested Dollar"
              data={snapChartData}
              dropdownOptions={[
                { value: "Alphabetical" },
                { value: "Highest to lowest" },
                { value: "Lowest to highest" },
              ]}
              dropdownValue={snapChartSortValue}
              setDropdownValue={setSnapChartSortValue}
              valueFormatter={value => {
                return value ? currencyFormatter.format(value) : "--";
              }}
              pngDownloadClassName=".nextMediaDollarSection .left"
            />
          )}
        </div>
        <div className="right">
          <ChartContainer
            enableHoverDesign
            height={405}
            title={`${outcomeVariable === "revenue" ? "ROAS" : "CPA"} and Next Best Dollar`}
            subHeader="Marginal returns reflect the steepness of the saturation curve at current investment levels"
            rightActions={
              <DownloadDropdown
                menuOptions={["XLSX"]}
                size="sm"
                onClickOptions={[excelDownloadROI]}
              />
            }
          >
            <div ref={containerRef} style={{ width: "100%" }}>
              {!R.isEmpty(roiTableData) &&
                !R.isEmpty(dimensionData) &&
                !R.isEmpty(tableHeaders) &&
                !rerenderTable && (
                  <MetricsTable
                    aggregateData={[]}
                    dimensionColumns={DimensionColumns}
                    dimensionData={dimensionData}
                    tableData={roiTableData}
                    dataColumns={tableHeaders}
                    overrideParams={{
                      maxColWidth: 130,
                      rowHeight: ROW_HEIGHT,
                    }}
                    width={tableWidth}
                    height={TABLE_HEIGHT}
                    columnMetaDataMap={{
                      nextDollarEffectiveness: {
                        displayName: "Next Dollar Effectiveness",
                        overlayText:
                          "Formally, this is the slope of the line tangent to the response\nsaturation curve at the current level of investment for the channel.",
                        overlayPlacement: "left center",
                      },
                    }}
                  />
                )}
            </div>
          </ChartContainer>
        </div>
      </div>
    </WidgetContainer>
  );
};

export default NextMediaDollar;
